import type { MockServer, MockServerRoute } from '@core/entities/MockServer';
import type {
  CreateMockServerPayload,
  MockRoutePayload,
  MockServerRepository,
  UpdateMockServerPayload,
} from '@core/repositories/MockServerRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';

interface MockServerRouteResponse {
  id: string;
  method: string;
  path: string;
  status_code: number;
  response_headers: Record<string, string> | null;
  response_body: string | null;
  is_enabled: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

interface MockServerResponse {
  id: string;
  workspace_id: string | null;
  collection_id: string | null;
  name: string;
  base_url: string;
  full_url: string;
  is_active: boolean;
  delay_ms: number;
  route_count: number | null;
  routes: MockServerRouteResponse[];
  created_by: { id: string; name: string } | null;
  abilities: { update: boolean; delete: boolean };
  created_at: string;
  updated_at: string;
}

const toRoute = (payload: MockServerRouteResponse): MockServerRoute => ({
  id: payload.id,
  method: payload.method,
  path: payload.path,
  statusCode: payload.status_code,
  responseHeaders: payload.response_headers,
  responseBody: payload.response_body,
  isEnabled: payload.is_enabled,
  position: payload.position,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

const toMockServer = (payload: MockServerResponse): MockServer => ({
  id: payload.id,
  workspaceId: payload.workspace_id,
  collectionId: payload.collection_id,
  name: payload.name,
  baseUrl: payload.base_url,
  fullUrl: payload.full_url,
  isActive: payload.is_active,
  delayMs: payload.delay_ms,
  routeCount: payload.route_count,
  routes: (payload.routes ?? []).map(toRoute),
  createdBy: payload.created_by,
  abilities: payload.abilities,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

const toRoutePayload = (payload: MockRoutePayload) => ({
  method: payload.method,
  path: payload.path,
  status_code: payload.statusCode,
  response_headers: payload.responseHeaders,
  response_body: payload.responseBody,
  is_enabled: payload.isEnabled,
});

export class MockServerRepositoryImpl implements MockServerRepository {
  async list(workspaceId: string): Promise<MockServer[]> {
    try {
      const response = await httpClient.get<{ data: MockServerResponse[] }>(
        `/workspaces/${workspaceId}/mock-servers`,
      );
      return response.data.data.map(toMockServer);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async get(id: string): Promise<MockServer> {
    try {
      const response = await httpClient.get<{ data: MockServerResponse }>(`/mock-servers/${id}`);
      return toMockServer(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(workspaceId: string, payload: CreateMockServerPayload): Promise<MockServer> {
    try {
      const response = await httpClient.post<{ data: MockServerResponse }>(
        `/workspaces/${workspaceId}/mock-servers`,
        {
          name: payload.name,
          collection_id: payload.collectionId,
        },
      );
      return toMockServer(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async update(id: string, payload: UpdateMockServerPayload): Promise<MockServer> {
    try {
      const response = await httpClient.put<{ data: MockServerResponse }>(`/mock-servers/${id}`, {
        name: payload.name,
        is_active: payload.isActive,
        delay_ms: payload.delayMs,
      });
      return toMockServer(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/mock-servers/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async createRoute(mockServerId: string, payload: MockRoutePayload): Promise<MockServerRoute> {
    try {
      const response = await httpClient.post<{ data: MockServerRouteResponse }>(
        `/mock-servers/${mockServerId}/routes`,
        toRoutePayload(payload),
      );
      return toRoute(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async updateRoute(routeId: string, payload: MockRoutePayload): Promise<MockServerRoute> {
    try {
      const response = await httpClient.put<{ data: MockServerRouteResponse }>(
        `/mock-server-routes/${routeId}`,
        toRoutePayload(payload),
      );
      return toRoute(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async removeRoute(routeId: string): Promise<void> {
    try {
      await httpClient.delete(`/mock-server-routes/${routeId}`);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
